import { createSelector } from '@reduxjs/toolkit';
import { memoize } from 'lodash-es';

import { AppState } from '@/redux/helper';
import {
  selectDayPassHabitatsHistory,
  selectHasExplorerFeature,
  selectSupportedHabitatsHistory,
} from '@/redux/selectors/users';

import { ProductPromptType, SetProductsPromptModalStatePayload } from './types';

export const createSelectProductsPromptModalStateForSponsorship = memoize((habitatId: string) =>
  createSelector(
    selectSupportedHabitatsHistory,
    selectHasExplorerFeature,
    (state: AppState) => state.user.subscription?.habitatsAccessUntil,
    (supportedHabitatsHistory, hasExplorerFeature, habitatsAccessUntil): SetProductsPromptModalStatePayload => {
      const hasAccess = !!habitatsAccessUntil?.[habitatId] && Date.parse(habitatsAccessUntil[habitatId]) > Date.now();

      return {
        habitatId,
        primaryProductType: 'sponsorship',
        secondaryProductType: hasExplorerFeature
          ? undefined
          : supportedHabitatsHistory.has(habitatId) && !hasAccess
          ? 'single-habitat-day-pass'
          : 'explorer-pass',
      };
    },
  ),
);

const isActive = <K extends string | number | symbol>(map: Record<K, string> | undefined, key: K) => {
  if (!map?.[key]) {
    return false;
  }
  return Date.parse(map[key]) > Date.now();
};

export const createSelectProductsPromptModalState = memoize((habitatId: string | undefined) =>
  createSelector(
    selectDayPassHabitatsHistory,
    selectSupportedHabitatsHistory,
    (state: AppState) => state.user.persona,
    (state: AppState) => state.user.subscription?.featuresUntil,
    (state: AppState) => state.user.subscription?.habitatsAccessUntil,
    (
      dayPassHabitatsHistory,
      supportedHabitatsHistory,
      persona,
      featuresUntil,
      habitatsAccessUntil,
    ): SetProductsPromptModalStatePayload => {
      const isEducator = persona === 'educator';
      const isExplorerFeatureExpired = featuresUntil?.explorer && !isActive(featuresUntil, 'explorer');

      if (!habitatId) {
        return {
          primaryProductType: 'all-habitats-day-pass',
          secondaryProductType: isExplorerFeatureExpired ? 'explorer-pass' : undefined,
          forEducator: isEducator,
        };
      }

      // If user has active day pass or supported habitat, show the explorer pass
      const hasActiveHabitatAccess = Object.values(habitatsAccessUntil ?? {}).some(
        (value) => Date.parse(value) > Date.now(),
      );
      if (hasActiveHabitatAccess) {
        return isEducator
          ? {
              habitatId,
              primaryProductType: 'single-habitat-day-pass',
              secondaryProductType: 'educator-pass',
              forEducator: true,
            }
          : {
              habitatId,
              primaryProductType: 'explorer-pass',
              secondaryProductType: 'single-habitat-day-pass',
            };
      }

      // If the explorer feature was active but is now expired, show the explorer pass
      if (isExplorerFeatureExpired) {
        return isEducator
          ? {
              primaryProductType: 'educator-pass',
              secondaryProductType: 'all-habitats-day-pass',
              forEducator: true,
            }
          : {
              habitatId,
              primaryProductType: 'all-habitats-day-pass',
              secondaryProductType: 'explorer-pass',
            };
      }

      // If the habitat was accessible by day pass, show the sponsorship and single habitat day pass
      if (!isEducator && (dayPassHabitatsHistory.has(habitatId) || supportedHabitatsHistory.has(habitatId))) {
        return {
          habitatId,
          primaryProductType: 'sponsorship',
          secondaryProductType: 'single-habitat-day-pass',
        };
      }

      // default to all habitats day pass and single habitat day pass
      return {
        habitatId,
        primaryProductType: 'all-habitats-day-pass',
        secondaryProductType: 'single-habitat-day-pass',
        forEducator: isEducator,
      };
    },
  ),
);

export const getDefaultProductsPromptModalState = (
  productPromptType: ProductPromptType,
  habitatId: string,
  state: AppState,
): SetProductsPromptModalStatePayload => {
  switch (productPromptType) {
    case 'explorer':
      return {
        primaryProductType: 'explorer-pass',
      };
    case 'educator':
      return {
        primaryProductType: 'educator-pass',
      };
    case 'premium':
      return {
        primaryProductType: 'explorer-pass',
        secondaryProductType: 'educator-pass',
      };
    case 'all-access':
      return {
        primaryProductType: 'all-habitats-day-pass',
      };
    case 'day-pass':
      return {
        habitatId,
        primaryProductType: 'all-habitats-day-pass',
        secondaryProductType: 'single-habitat-day-pass',
      };
    case 'sponsor':
      return createSelectProductsPromptModalStateForSponsorship(habitatId)(state);
    default:
      return createSelectProductsPromptModalState(habitatId)(state);
  }
};
